import { motion } from 'motion/react'
import { Award, Users, Target, TrendingUp } from 'lucide-react'
import { SEO } from '@/lib/seo'
import { Badge } from '@/components/ui'
import { CTASection, Testimonials } from '@/components/sections'
import { companyInfo, whyChooseUs } from '@/data/company'

const icons = [Award, Users, Target, TrendingUp]

export function About() {
  return (
    <>
      <SEO
        title="About Us"
        description="Learn about UC Advisors - your strategic partner for UCaaS, CCaaS, Network, SD-WAN, and Cybersecurity solutions with 30+ years of combined experience."
      />

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-50" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge variant="glow" className="mb-6">About UC Advisors</Badge>

            <h1 className="text-display-md md:text-display-lg lg:text-display-xl font-heading font-bold text-white mb-6">
              Your Strategic Partner in{' '}
              <span className="text-gradient">Business Excellence</span>
            </h1>

            <p className="text-xl text-slate-300 mb-8">
              {companyInfo.description}
            </p>

            <p className="text-lg text-slate-400">
              Our mission is to revolutionize the way businesses operate by delivering cutting-edge solutions that leverage the latest advancements in technology and strategic thinking.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary-600/30">
        <div className="container-custom">
          <div className="grid grid-cols-3 gap-8">
            {companyInfo.stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-heading font-bold text-gradient mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">What We Do</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Technology Broker Agnostic to All Service Providers
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              We work with all major technology providers to find the best solutions for your specific needs—not the solutions that benefit us the most. Our vendor-agnostic approach means you get unbiased recommendations backed by data.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Business Budget Optimization',
                description: 'We analyze your current technology spend and identify opportunities to reduce costs while improving capabilities.',
              },
              {
                title: 'Your 1-Stop Shop for All IT Needs',
                description: 'From communications to cybersecurity, we provide comprehensive technology solutions under one roof.',
              },
              {
                title: 'Data-Backed Recommendations',
                description: 'Every recommendation we make is supported by detailed analysis and industry benchmarks.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card-hover p-8 rounded-2xl text-center"
              >
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-accent-500 to-electric-500 flex items-center justify-center mb-6 shadow-glow">
                  <span className="text-2xl font-heading font-bold text-white">{index + 1}</span>
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-primary-600/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Why Choose UC Advisors</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              What Sets Us Apart
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = icons[index % icons.length]
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-card-hover p-8 rounded-2xl"
                >
                  <div className="flex gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500/20 to-electric-500/20 flex items-center justify-center">
                        <Icon className="w-7 h-7 text-accent-400" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-white mb-3">
                        {item.title}
                      </h3>
                      <p className="text-slate-400">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Badge className="mb-4">Certifications</Badge>
            <h2 className="text-display-sm md:text-display-md font-heading font-bold text-white mb-4">
              Zoom Certified Partner
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              We're proud to be certified by Zoom as both installers and support specialists, ensuring you get expert implementation and ongoing support.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {companyInfo.certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-8 rounded-2xl text-center"
              >
                <div className="w-20 h-20 mx-auto rounded-2xl bg-blue-500/20 flex items-center justify-center mb-6">
                  <span className="text-blue-400 font-bold text-3xl">Z</span>
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-3">
                  {cert.name}
                </h3>
                <p className="text-slate-400">{cert.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <CTASection />
    </>
  )
}

